import { maitoSetupOffer } from "./setupOffer.ts";

export type SlackMessage = { name: string; time: string; text: string };
export type ChooseOption = { name: string; reasons: string[] };
export type PriceOption = { name: string; price: string; details: string[] };

const fence = /^```(slack|choose|price|setup)\r?\n([\s\S]*?)\r?\n```[ \t]*$/gm;
const groups = (source: string) =>
  source
    .trim()
    .split(/\r?\n[ \t]*\r?\n/)
    .map((group) => group.split(/\r?\n/).map((line) => line.trim()));

/**
 * Parse a ```slack block: an optional `#channel` line, then messages separated by blank
 * lines, each starting with `Name | time` followed by the message text.
 */
export function parseSlack(source: string): { channel: string; messages: SlackMessage[] } {
  const channelLine = /^#([\w-]+)[ \t]*\r?\n/.exec(source.trim());
  const channel = channelLine?.[1] ?? "general";
  const body = channelLine ? source.trim().slice(channelLine[0].length) : source;
  const messages = groups(body).map(([header = "", ...lines]) => {
    const match = /^(.+?)\s+\|\s+(.+)$/.exec(header);
    const text = lines.join("\n").trim();
    if (!match || !text)
      throw new Error(`Invalid slack block: "${header}" needs "Name | time" and message text`);
    return { name: match[1]!, time: match[2]!, text };
  });
  return { channel, messages };
}

/** Parse a ```choose block: two groups, each a product name followed by `- reason` lines. */
export function parseChoose(source: string): ChooseOption[] {
  const options = groups(source).map(([name = "", ...lines]) => {
    if (!name || !lines.length || lines.some((line) => !line.startsWith("- ")))
      throw new Error(`Invalid choose block: "${name}" needs a name followed by "- " reasons`);
    return { name, reasons: lines.map((line) => line.slice(2).trim()) };
  });
  if (options.length !== 2) throw new Error("Invalid choose block: list exactly two products");
  return options;
}

/** Parse a ```price block: two groups, each a product name, a price line, then `- ` details. */
export function parsePrice(source: string): PriceOption[] {
  const options = groups(source).map(([name = "", price = "", ...lines]) => {
    if (!name || !price || price.startsWith("- ") || lines.some((line) => !line.startsWith("- ")))
      throw new Error(
        `Invalid price block: "${name}" needs a name, a price line, and "- " details`,
      );
    return { name, price, details: lines.map((line) => line.slice(2).trim()) };
  });
  if (options.length !== 2) throw new Error("Invalid price block: list exactly two products");
  return options;
}

/** Select the built-in Maito setup offer without duplicating its price or service details. */
export function parseSetup(source: string) {
  if (source.trim() !== "Maito") throw new Error('Invalid setup block: use "Maito"');
  return maitoSetupOffer;
}

const parsers = { slack: parseSlack, choose: parseChoose, price: parsePrice, setup: parseSetup };

/** Fail the build on malformed blocks instead of publishing an empty section. */
export function validateCompareBlocks(markdown: string) {
  for (const [, kind, source] of markdown.matchAll(fence))
    parsers[kind as keyof typeof parsers](source!);
}

const faqHeading = /^##\s.*frequently asked questions/im;
const plainText = (markdown: string) =>
  markdown
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\*\*|`/g, "")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Read the questions from a comparison page's FAQ card: an H2 containing "frequently asked
 * questions", then each `### Question?` followed by its answer. Trailing source links are
 * dropped from answers, as on the page. Used for FAQPage structured data.
 */
export function parseFaq(markdown: string) {
  const start = markdown.search(faqHeading);
  if (start < 0) return [];
  const card = markdown.slice(start).split(/\r?\n(?=##\s)/)[0]!;
  return card
    .split(/\r?\n(?=###\s)/)
    .slice(1)
    .map((block) => {
      const [heading = "", ...answer] = block.split(/\r?\n/);
      const text = answer.join("\n").replace(/(?:[\s,]*\[[^\]]+\]\([^)]+\))+[\s.]*$/, "");
      return { question: plainText(heading.slice(4)), answer: plainText(text) };
    })
    .filter((item) => item.question && item.answer);
}

const markWords = { "✓": "Yes", "✗": "No", "~": "Partly" };

/**
 * Markdown export: drop `{wide}` and `{xl}` layout markers, spell out table marks as Yes/No/Partly,
 * label conversations as illustrations, and
 * turn choices and prices into lists, and include the setup offer's service and action.
 */
export function exportCompareBlocks(markdown: string) {
  return markdown
    .replace(/^(##\s.*?)\s*\{(?:wide|xl)\}[ \t]*$/gm, "$1")
    .replace(
      /(\|\s*)([✓✗~])\s+(?=[^|]*\|)/g,
      (_, cell: string, mark: keyof typeof markWords) => `${cell}${markWords[mark]}: `,
    )
    .replace(fence, (_, kind: string, source: string) => {
      if (kind === "setup") {
        const offer = parseSetup(source);
        return `## ${offer.title}\n\n${offer.articleDescription}\n\n${offer.price} ${offer.priceTerms}\n\n[${offer.actionLabel}](${offer.actionUrl})`;
      }
      return kind === "slack"
        ? `_Illustrative conversation:_\n\n${parseSlack(source)
            .messages.map(({ name, text }) => `> **${name}:** ${text.split("\n").join("\n> ")}`)
            .join("\n>\n")}`
        : kind === "price"
          ? parsePrice(source)
              .map(
                ({ name, price, details }) =>
                  `**${name}: ${price}**\n\n${details.map((detail) => `- ${detail}`).join("\n")}`,
              )
              .join("\n\n")
          : parseChoose(source)
              .map(
                ({ name, reasons }) =>
                  `**Choose ${name} if:**\n\n${reasons.map((reason) => `- ${reason}`).join("\n")}`,
              )
              .join("\n\n");
    });
}
